import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Products from "@/pages/Products";
import Quotes from "@/pages/Quotes";
import QuoteForm from "@/pages/QuoteForm";
import Orders from "@/pages/Orders";
import WarehouseView from "@/pages/WarehouseView";
import ProductionView from "@/pages/ProductionView";
import ProductionByProduct from "@/pages/ProductionByProduct";
import Users from "@/pages/Users";
import Accounting from "@/pages/Accounting";
import Finance from "@/pages/Finance";
import Personnel from "@/pages/Personnel";
import Settings from "@/pages/Settings";
import Logs from "@/pages/Logs";
import HSE from "@/pages/HSE";
import PageHeader from "@/components/PageHeader";
import { HseExpiringWidget } from "@/components/HseExpiringWidget";

function RootRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-8 text-sm font-mono">Yükleniyor…</div>;
  if (!user) return <Navigate to="/giris" replace />;
  if (user.role === "warehouse") return <Navigate to="/depo" replace />;
  if (user.role === "production") return <Navigate to="/uretim" replace />;
  if (user.role === "accounting") return <Navigate to="/muhasebe" replace />;
  if (user.role === "hse") return (
    <div className="space-y-6" data-testid="hse-home">
      <PageHeader kicker="Panel" title="İSG Özeti" />
      <HseExpiringWidget />
    </div>
  );
  return <Dashboard />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/giris" element={<Login />} />
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/teklifler" element={<ProtectedRoute roles={["sales"]}><Quotes /></ProtectedRoute>} />
            <Route path="/teklifler/yeni" element={<ProtectedRoute roles={["sales"]}><QuoteForm /></ProtectedRoute>} />
            <Route path="/teklifler/:id" element={<ProtectedRoute roles={["sales"]}><QuoteForm /></ProtectedRoute>} />
            <Route path="/siparisler" element={<ProtectedRoute roles={["sales"]}><Orders /></ProtectedRoute>} />
            <Route path="/siparisler/:id/duzenle" element={<ProtectedRoute roles={["sales"]}><QuoteForm /></ProtectedRoute>} />
            <Route path="/depo" element={<ProtectedRoute roles={["warehouse"]}><WarehouseView /></ProtectedRoute>} />
            <Route path="/uretim" element={<ProtectedRoute roles={["production"]}><ProductionView /></ProtectedRoute>} />
            <Route path="/uretim/urun-bazli" element={<ProtectedRoute roles={["production"]}><ProductionByProduct /></ProtectedRoute>} />
            <Route path="/muhasebe" element={<ProtectedRoute roles={["accounting", "sales"]}><Accounting /></ProtectedRoute>} />
            <Route path="/finans" element={<ProtectedRoute roles={["admin", "sub_admin"]}><Finance /></ProtectedRoute>} />
            <Route path="/personel" element={<ProtectedRoute roles={["admin", "sub_admin", "accounting"]}><Personnel /></ProtectedRoute>} />
            <Route path="/isg" element={<ProtectedRoute roles={["admin", "sub_admin", "hse"]}><HSE /></ProtectedRoute>} />
            <Route path="/musteriler" element={<ProtectedRoute roles={["sales"]}><Customers /></ProtectedRoute>} />
            <Route path="/musteriler/:id" element={<ProtectedRoute roles={["sales"]}><CustomerDetail /></ProtectedRoute>} />
            <Route path="/urunler" element={<Products />} />
            <Route path="/kullanicilar" element={<ProtectedRoute roles={["admin"]}><Users /></ProtectedRoute>} />
            <Route path="/ayarlar" element={<ProtectedRoute roles={["admin"]}><Settings /></ProtectedRoute>} />
            <Route path="/loglar" element={<ProtectedRoute roles={["admin"]}><Logs /></ProtectedRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
